import uploadcare from 'uploadcare-widget';
import uploadcareTabEffects from 'uploadcare-widget-tab-effects';
import mime from 'mime-types';
import { BufferTracker } from '@bufferapp/buffer-tracking-browser-ts';
import ComposerStore from '~/composer/composer/stores/ComposerStore';
import { selectCurrentOrganizationId } from '~/organizations/selectors';
import { store } from '~/store';
import { getDebugLogger } from '~/utils/getDebugLogger';
import { fileFromUrl } from '~/uploads/lib/remoteFile';
import { UploadTypes } from '~/constants';
import {
  gifsAllowed,
  ServiceCropMap,
  UploadcareIntegrations,
  UploadcareSources,
} from './constants';
import giphyIntegrationTab from './integrations/giphy/giphyIntegrationTab';
import { PostTypeStory } from '../post/constants';

const debug = getDebugLogger('uploadcare');

// This is required because 'ideas' is not allowed in the tracking plan
const getChannel = (serviceId) =>
  serviceId === 'ideas' ? 'multiple' : serviceId;

const onUploadDone = (
  result,
  onFileReady,
  onError,
  onUploadcareUploadFinished,
  isEdit,
  draftData,
  serviceId,
  source,
  integration,
  context
) => {
  try {
    if (isEdit) {
      draftData.deleteCallback?.();
    }
  } catch (e) {
    debug(e.message, e);
  }

  result
    .promise()
    .done(async (response) => {
      try {
        const extension = mime.extension(response.mimeType);
        const filename =
          !extension || response.name.endsWith(`.${extension}`)
            ? response.name
            : `${response.name}.${extension}`;

        const file = await fileFromUrl({
          remoteUrl: response.cdnUrl,
          name: filename,
        });

        onFileReady(file, {
          name: 'uploadcare',
        });

        onUploadcareUploadFinished();

        const media = file.type.includes('image') ? 'photo' : 'video';
        BufferTracker.uploadcareUploadFinished({
          organizationId: selectCurrentOrganizationId(store.getState()),
          clientName: 'publishWeb',
          product: 'publish',
          channel: getChannel(serviceId),
          source,
          integration,
          media,
          context,
        });
      } catch (e) {
        debug(e.message, e);
        onError({ message: e.message });
      }
    })
    .fail((message) => {
      /**
       * If error message = 'upload', it means the file size is too big (over 100MB,
       * which is allowed by our plan). It's returned by Uploadcare servers, so we change
       * it to something more meaningful
       */
      if (message === 'upload') {
        message = 'File size should be less than 100MB';
      }

      try {
        onUploadcareUploadFinished();

        debug(message);
        onError({ message });
      } catch (e) {
        debug(e.message, e);
      }
    });
};

const imagesAndVideosOnly = (fileInfo) => {
  if (
    fileInfo.mimeType !== null &&
    fileInfo.isImage === false &&
    !fileInfo.mimeType.includes('video')
  ) {
    throw new Error('Files other than videos and images are not supported.');
  }
};

const fileSizeStories = (fileInfo) => {
  if (
    fileInfo.size !== null &&
    fileInfo.size > 8 * 1024 * 1024 &&
    fileInfo.isImage === true
  ) {
    throw new Error('Image has to be less than 8MB.');
  }

  if (
    fileInfo.size !== null &&
    fileInfo.size > 50 * 1024 * 1024 &&
    fileInfo.mimeType !== null &&
    fileInfo.mimeType.includes('video')
  ) {
    throw new Error('Video has to be less than 50MB.');
  }

  if (
    fileInfo.size !== null &&
    fileInfo.size > 3 * 1024 * 1024 &&
    fileInfo.mimeType !== null &&
    fileInfo.mimeType.includes('image/gif')
  ) {
    throw new Error('GIF has to be less than 3MB.');
  }
};

const fileSize = (fileInfo) => {
  if (
    fileInfo.size !== null &&
    fileInfo.size > 10 * 1024 * 1024 &&
    fileInfo.isImage === true
  ) {
    throw new Error('Image has to be less than 10MB.');
  }

  if (
    fileInfo.size !== null &&
    fileInfo.size > 1024 * 1024 * 1024 &&
    fileInfo.mimeType !== null &&
    fileInfo.mimeType.includes('video')
  ) {
    throw new Error('Video has to be less than 1GB.');
  }

  if (
    fileInfo.size !== null &&
    fileInfo.size > 3 * 1024 * 1024 &&
    fileInfo.mimeType !== null &&
    fileInfo.mimeType.includes('image/gif')
  ) {
    throw new Error('GIF has to be less than 3MB.');
  }
};

const gifsNotAllowed = (fileInfo) => {
  if (fileInfo.mimeType !== null && fileInfo.mimeType.includes('image/gif')) {
    throw new Error('Gifs are not supported for this network.');
  }
};

const webmMovAndMp4OnlyAllowed = (fileInfo) => {
  if (
    fileInfo.mimeType !== null &&
    !fileInfo.mimeType.includes('video/mp4') &&
    !fileInfo.mimeType.includes('video/mov') &&
    !fileInfo.mimeType.includes('video/webm')
  ) {
    throw new Error(
      'For this network, we support only MP4, MOV and WebM videos.'
    );
  }
};

const imagesOnlyAllowed = (fileInfo) => {
  if (
    fileInfo.mimeType !== null &&
    (fileInfo.mimeType.includes('video') || fileInfo.mimeType.includes('gif'))
  ) {
    throw new Error('For this network, we support only image upload.');
  }
};

const imagesAndGifsOnlyAllowed = (fileInfo) => {
  if (fileInfo.mimeType !== null && fileInfo.mimeType.includes('video')) {
    throw new Error('For this network, we support only image or gif upload.');
  }
};

export const getUploadCareEditOptions = ({ serviceId, updateType = null }) => {
  const uploadcareStartOptions = {
    effects: [
      'blur',
      'sharp',
      'grayscale',
      'crop',
      'rotate',
      'enhance',
      'mirror',
      'flip',
      'invert',
    ],
  };
  const uploadcareOptions = {
    publicKey: process.env.UPLOADCARE_KEY,
    imagesOnly: true,
    tabs: 'preview',
    previewStep: true,
    effects: 'all',
  };

  if (serviceId === 'omni') {
    const enabledMaps = ComposerStore.getEnabledDrafts()
      .map((draft) => draft.id)
      .map((serviceName) => ServiceCropMap[serviceName]);
    const intersectedCrops = [
      ...enabledMaps.reduce((a, b) => a.filter((c) => b.includes(c))),
      ...ServiceCropMap.omni,
    ];
    uploadcareOptions.crop = [...new Set(intersectedCrops)].join(',');
  } else {
    uploadcareOptions.crop = ServiceCropMap[serviceId].join(',');
    if (updateType === PostTypeStory) {
      uploadcareOptions.crop = `${uploadcareOptions.crop},1080x1920 minimum`;
    }
  }

  return { uploadcareStartOptions, uploadcareOptions };
};

export const uploadcareValidators = {
  fileSize,
  gifsNotAllowed,
  imagesOnlyAllowed,
  imagesAndVideosOnly,
  imagesAndGifsOnlyAllowed,
  webmMovAndMp4OnlyAllowed,
};

export const getUploadCareIntegrationsOptions = ({
  serviceId,
  integration,
  updateType = null,
}) => {
  const uploadcareOptions = {
    publicKey: process.env.UPLOADCARE_KEY,
    inputAcceptTypes: 'image/*,video/*',
    tabs: integration,
    validators: [imagesAndVideosOnly],
  };

  if (updateType === PostTypeStory) {
    uploadcareOptions.validators.push(fileSizeStories);
  } else {
    uploadcareOptions.validators.push(fileSize);
  }

  if (!gifsAllowed.includes(serviceId)) {
    uploadcareOptions.validators.push(gifsNotAllowed);
  }

  if (serviceId === 'tiktok') {
    uploadcareOptions.validators.push(webmMovAndMp4OnlyAllowed);
  }

  if (serviceId === 'googlebusiness') {
    uploadcareOptions.validators.push(imagesOnlyAllowed);
  }

  if (serviceId === 'startPage') {
    uploadcareOptions.validators.push(imagesAndGifsOnlyAllowed);
  }

  return {
    uploadcareOptions,
    uploadcareStartOptions: {},
  };
};

const openUploadcareModal = (
  serviceId,
  onFileReady,
  onError,
  integration,
  onUploadcareUploadStarted,
  onUploadcareUploadFinished,
  source,
  draftData,
  updateType = String(UploadTypes.MEDIA),
  context = 'publishComposer'
) => {
  const isEdit = source === UploadcareSources.EDIT;
  if (isEdit) {
    BufferTracker.uploadcareEditClicked({
      organizationId: selectCurrentOrganizationId(store.getState()),
      clientName: 'publishWeb',
      product: 'publish',
      channel: getChannel(serviceId),
      source,
      integration,
      context,
    });
  } else {
    BufferTracker.uploadcareIntegrationButtonClicked({
      organizationId: selectCurrentOrganizationId(store.getState()),
      clientName: 'publishWeb',
      product: 'publish',
      channel: getChannel(serviceId),
      source,
      integration,
      context,
    });
  }

  // 1. Init options
  const { uploadcareStartOptions, uploadcareOptions } = isEdit
    ? getUploadCareEditOptions({ serviceId, updateType })
    : getUploadCareIntegrationsOptions({ serviceId, integration, updateType });

  // 2. Init Uploadcare
  uploadcare.start(uploadcareStartOptions);

  // 3: If edit enabled, register "Edit" tab
  if (isEdit) {
    uploadcare.registerTab('preview', uploadcareTabEffects);
  }

  // 4: Register custom integrations
  uploadcare.registerTab(UploadcareIntegrations.GIPHY, giphyIntegrationTab);

  // 5. Open dialog
  const dialog = uploadcare.openDialog(null, uploadcareOptions);
  BufferTracker.uploadcareModalOpened({
    organizationId: selectCurrentOrganizationId(store.getState()),
    clientName: 'publishWeb',
    product: 'publish',
    channel: getChannel(serviceId),
    source,
    integration,
    context,
  });

  // 6: If edit enabled, fetch the image to Uploadcare
  if (isEdit) {
    dialog.addFiles('url', [
      [
        draftData.url,
        {
          source: 'url-tab',
        },
      ],
    ]);
  }

  dialog.done((result) => {
    onUploadcareUploadStarted();
    BufferTracker.uploadcareUploadStarted({
      organizationId: selectCurrentOrganizationId(store.getState()),
      clientName: 'publishWeb',
      product: 'publish',
      channel: getChannel(serviceId),
      source,
      integration,
      context,
    });
    onUploadDone(
      result,
      onFileReady,
      onError,
      onUploadcareUploadFinished,
      isEdit,
      draftData,
      serviceId,
      source,
      integration,
      context
    );
  });

  dialog.fail((result) => {
    debug('Failed uploadcare dialog', result);
  });
};

export { openUploadcareModal };
